import PropTypes from 'prop-types';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { batch } from 'react-redux';

// material-ui
import { TextField, Box } from '@mui/material';

// third party
import { useIntl } from 'react-intl';

// project imports
import { useSelector, useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { ApiBusinessOpportunitiesGetCollection } from 'store/app/workmgmt/busopp';
import Autocomplete from 'ui-component/extended/Autocomplete';

// ==============================|| SELECT WORK MGMT WORK ORDER FIELD ||============================== //

export const renderBusinessOpportunity = (busOpp) => {
    if (!busOpp) return '';
    if (typeof workOrder === 'string') return busOpp;
    return 'code' in (busOpp || {}) && 'name' in (busOpp || {}) ? `[${busOpp.code}] ${busOpp.name}` : '';
};

const BusinessOpportunitySelect = ({
    value,
    onChange = () => {},
    validationErrors = null,
    baseId = '',
    enableSearch = true,
    loadData = false,
    baseFilterGroups = [],
    ...others
}) => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const { list: businessOpportunityList, quickSearchFilterDef: businessOpportunityQuickSearchFilterDef } = useSelector(
        (state) => state.businessOpportunity
    );

    const [isLoadingBusinessOpportunity, setIsLoadingBusinessOpportunity] = useState(false);
    const [userInputBusinessOpportunity, setUserInputBusinessOpportunity] = useState('');
    const [businessOpportunities, setBusinessOpportunities] = useState(businessOpportunityList || []);

    const businessOpportunityFilterRows = useMemo(
        () =>
            businessOpportunityQuickSearchFilterDef?.fields?.map((fd) => ({
                field: fd,
                operator: 'like',
                value: userInputBusinessOpportunity
            })),
        [businessOpportunityQuickSearchFilterDef, userInputBusinessOpportunity]
    );

    const handleLoadBusinessOpportunity = useCallback(() => {
        setIsLoadingBusinessOpportunity(true);
        ApiBusinessOpportunitiesGetCollection({
            filters: baseFilterGroups,
            successCallback: (response) =>
                batch(() => {
                    setIsLoadingBusinessOpportunity(false);
                    setBusinessOpportunities(response);
                    setUserInputBusinessOpportunity('');
                }),
            errorCallback: (error) =>
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                ),
            updateStore: false
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseFilterGroups]);

    useEffect(() => {
        if (loadData) {
            handleLoadBusinessOpportunity();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    useEffect(() => {
        if (enableSearch && userInputBusinessOpportunity?.length >= 3) {
            setIsLoadingBusinessOpportunity(true);
            ApiBusinessOpportunitiesGetCollection({
                filters: [...baseFilterGroups, { filterType: 'or', filterRows: businessOpportunityFilterRows }],
                successCallback: (response) =>
                    batch(() => {
                        setIsLoadingBusinessOpportunity(false);
                        setBusinessOpportunities(response);
                    }),
                errorCallback: (error) =>
                    batch(() => {
                        setIsLoadingBusinessOpportunity(false);
                        dispatch(
                            openSnackbar({
                                open: true,
                                message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                                variant: 'alert',
                                alert: { color: 'error' }
                            })
                        );
                    }),
                updateStore: false
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableSearch, businessOpportunityFilterRows]);

    return (
        <Autocomplete
            id={`${baseId}-businessOpportunity`}
            value={value}
            onChange={(value) =>
                batch(() => {
                    onChange(value);
                    setUserInputBusinessOpportunity(renderBusinessOpportunity(value));
                })
            }
            inputValue={userInputBusinessOpportunity || renderBusinessOpportunity(value)}
            onInputChange={(value, reason) => {
                if (reason === 'input') {
                    setUserInputBusinessOpportunity(value);
                }
                if (reason === 'clear') {
                    handleLoadBusinessOpportunity();
                }
            }}
            loading={isLoadingBusinessOpportunity}
            options={businessOpportunities}
            freeSolo={enableSearch}
            getOptionLabel={(option) => renderBusinessOpportunity(option)}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    {renderBusinessOpportunity(option)}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputProps={{ ...params.inputProps }}
                    error={Boolean(validationErrors)}
                    helperText={validationErrors}
                />
            )}
            {...others}
        />
    );
};

BusinessOpportunitySelect.propTypes = {
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
    onChange: PropTypes.func,
    validationErrors: PropTypes.any,
    baseId: PropTypes.string,
    enableSearch: PropTypes.bool,
    loadData: PropTypes.bool,
    baseFilterGroups: PropTypes.array
};

export default BusinessOpportunitySelect;
